import { useEffect, useState } from "react";
import { Select } from "antd";
import { serverFetch } from "@extensions";

/**
 * Общее для страниц кабинета: фильтр позиций по контексту группы.
 *
 * Состав группы, вариант "Показать" и отбор позиций повторялись
 * на страницах активных, неоплаченных и использованных покупок,
 * поэтому состояние и разметка собраны здесь.
 */

/** Ключи вариантов переключателя */
export const CONTEXT_ALL = "all";
export const CONTEXT_SELF = "self";

/**
 * Варианты переключателя: все позиции, свои и по одному на участника группы.
 */
export const buildContextOptions = (familyMembers) => [
  { key: CONTEXT_ALL, label: "Все" },
  { key: CONTEXT_SELF, label: "Мои" },
  ...familyMembers.map((m) => ({ key: m.id, label: m.name })),
];

/**
 * Количество позиций по каждому варианту переключателя.
 * Условия совпадают с отбором, поэтому числа в подписях
 * всегда равны длине показанного списка.
 */
export const countByContext = (items, familyMembers) => {
  const counts = {
    [CONTEXT_ALL]: items.length,
    [CONTEXT_SELF]: items.filter((item) => !item?.clientId || !familyMembers.some((m) => m.id === item.clientId)).length,
  };
  familyMembers.forEach((m) => {
    counts[m.id] = items.filter((item) => item?.clientId === m.id).length;
  });
  return counts;
};

/**
 * Кому адресована позиция: участник группы или сам представитель.
 * unknownInitial - что показать в аватаре, если clientId есть,
 * но участника с ним нет в группе.
 */
export const resolveForWhom = (clientId, familyMembers, unknownInitial = "?") => {
  const member = familyMembers.find((m) => m.id === clientId);
  return {
    member,
    isMember: Boolean(member),
    initial: member ? member.name.charAt(0) : "Я",
    name: member ? member.name : unknownInitial,
  };
};

/**
 * Ячейка "Для кого": аватар и имя участника группы либо "Я" представителя.
 */
export const ForWhomCell = ({ clientId, familyMembers, user, unknownInitial = "?", className = "cell-for" }) => {
  const { isMember, initial, name, member } = resolveForWhom(clientId, familyMembers, unknownInitial);
  const avatarSource = member || user;
  return (
    <div className={className}>
      <span className={`for-avatar ${isMember ? "" : "self"}`}>
        {avatarSource?.imageId ? (
          <img src={`${process.env.REACT_APP_API_BASE_URL}/api/image/${avatarSource.imageId}.jpg`} alt={name} />
        ) : (
          initial
        )}
      </span>
      <span>{name}</span>
    </div>
  );
};

/**
 * Переключатель "Показать" по контексту группы со счётчиками позиций.
 * items - полный состав позиций до отбора: по нему считаются количества.
 */
export const FamilyContextSelect = ({
  familyMembers,
  items,
  activeContext,
  onChange,
  className,
  labelClassName,
  selectClassName,
  label = "Показать:",
}) => {
  const counts = countByContext(items, familyMembers);
  const options = buildContextOptions(familyMembers);
  return (
    <div className={className}>
      <span className={labelClassName}>{label}</span>
      <Select
        className={selectClassName}
        value={activeContext}
        onChange={onChange}
        options={options.map((opt) => ({
          value: opt.key,
          label: `${opt.label} (${counts[opt.key] ?? 0})`,
        }))}
      />
    </div>
  );
};

// Состояние фильтра по группе: состав группы, выбранный вариант и отбор позиций.
// Группу перечитываем при смене пользователя - состав мог измениться.
export const useFamilyContext = (user) => {
  const [familyMembers, setFamilyMembers] = useState([]);
  // Полные данные авторизованного клиента (включая ImageId) - для аватара в колонке "Для кого"
  const [selfClient, setSelfClient] = useState(null);
  // "all" - все, "self" - свои, иначе id участника группы
  const [activeContext, setActiveContext] = useState(CONTEXT_ALL);

  useEffect(() => {
    if (!user) return;
    serverFetch("OnlineClient/FamilyList", { method: "GET" })
      .then((data) => setFamilyMembers(data || []))
      .catch(() => setFamilyMembers([]));
    // Аватар авторизованного клиента не входит в FamilyList, поэтому загружаем карточку клиента по информации текущего пользователя.
    serverFetch("OnlineClient/info", { method: "GET" })
      .then((data) => setSelfClient(data))
      .catch(() => setSelfClient(null));
  }, [user]);

  const hasFamily = familyMembers.length > 0;
  // Колонка "Для кого" нужна только когда показаны все позиции сразу
  const showForColumn = hasFamily && activeContext === CONTEXT_ALL;

  // Отбор позиций по выбранному варианту.
  // Состав списка небольшой, поэтому кэшировать результат не нужно:
  // фильтр дешевле мемоизации
  const filterByContext = (items) => {
    if (!hasFamily || activeContext === CONTEXT_ALL) return items;
    if (activeContext === CONTEXT_SELF) {
      return items.filter((item) => !item?.clientId || !familyMembers.some((m) => m.id === item.clientId));
    }
    return items.filter((item) => item?.clientId === activeContext);
  };

  return {
    familyMembers,
    selfClient,
    hasFamily,
    activeContext,
    setActiveContext,
    showForColumn,
    filterByContext,
  };
};
